/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { Alerts } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { SecurityCenter } from "../securityCenter";
import {
  Alert,
  AlertsListNextOptionalParams,
  AlertsListOptionalParams,
  AlertsListByResourceGroupNextOptionalParams,
  AlertsListByResourceGroupOptionalParams,
  AlertsListSubscriptionLevelByRegionNextOptionalParams,
  AlertsListSubscriptionLevelByRegionOptionalParams,
  AlertsListResourceGroupLevelByRegionNextOptionalParams,
  AlertsListResourceGroupLevelByRegionOptionalParams,
  AlertsListResponse,
  AlertsListByResourceGroupResponse,
  AlertsListSubscriptionLevelByRegionResponse,
  AlertsListResourceGroupLevelByRegionResponse,
  AlertsGetSubscriptionLevelOptionalParams,
  AlertsGetSubscriptionLevelResponse,
  AlertsGetResourceGroupLevelOptionalParams,
  AlertsGetResourceGroupLevelResponse,
  AlertsUpdateSubscriptionLevelStateToDismissOptionalParams,
  AlertsUpdateSubscriptionLevelStateToResolveOptionalParams,
  AlertsUpdateSubscriptionLevelStateToActivateOptionalParams,
  AlertsUpdateResourceGroupLevelStateToResolveOptionalParams,
  AlertsUpdateResourceGroupLevelStateToDismissOptionalParams,
  AlertsUpdateResourceGroupLevelStateToActivateOptionalParams,
  AlertSimulatorRequestBody,
  AlertsSimulateOptionalParams,
  AlertsListNextResponse,
  AlertsListByResourceGroupNextResponse,
  AlertsListSubscriptionLevelByRegionNextResponse,
  AlertsListResourceGroupLevelByRegionNextResponse
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing Alerts operations. */
export class AlertsImpl implements Alerts {
  private readonly client: SecurityCenter;

  /**
   * Initialize a new instance of the class Alerts class.
   * @param client Reference to the service client
   */
  constructor(client: SecurityCenter) {
    this.client = client;
  }

  /**
   * List all the alerts that are associated with the subscription
   * @param options The options parameters.
   */
  public list(
    options?: AlertsListOptionalParams
  ): PagedAsyncIterableIterator<Alert> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listPagingPage(options);
      }
    };
  }

  private async *listPagingPage(
    options?: AlertsListOptionalParams
  ): AsyncIterableIterator<Alert[]> {
    let result = await this._list(options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listPagingAll(
    options?: AlertsListOptionalParams
  ): AsyncIterableIterator<Alert> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * List all the alerts that are associated with the resource group
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  public listByResourceGroup(
    resourceGroupName: string,
    options?: AlertsListByResourceGroupOptionalParams
  ): PagedAsyncIterableIterator<Alert> {
    const iter = this.listByResourceGroupPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listByResourceGroupPagingPage(resourceGroupName, options);
      }
    };
  }

  private async *listByResourceGroupPagingPage(
    resourceGroupName: string,
    options?: AlertsListByResourceGroupOptionalParams
  ): AsyncIterableIterator<Alert[]> {
    let result = await this._listByResourceGroup(resourceGroupName, options);
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listByResourceGroupNext(
        resourceGroupName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listByResourceGroupPagingAll(
    resourceGroupName: string,
    options?: AlertsListByResourceGroupOptionalParams
  ): AsyncIterableIterator<Alert> {
    for await (const page of this.listByResourceGroupPagingPage(
      resourceGroupName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * List all the alerts that are associated with the subscription that are stored in a specific location
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param options The options parameters.
   */
  public listSubscriptionLevelByRegion(
    ascLocation: string,
    options?: AlertsListSubscriptionLevelByRegionOptionalParams
  ): PagedAsyncIterableIterator<Alert> {
    const iter = this.listSubscriptionLevelByRegionPagingAll(
      ascLocation,
      options
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listSubscriptionLevelByRegionPagingPage(
          ascLocation,
          options
        );
      }
    };
  }

  private async *listSubscriptionLevelByRegionPagingPage(
    ascLocation: string,
    options?: AlertsListSubscriptionLevelByRegionOptionalParams
  ): AsyncIterableIterator<Alert[]> {
    let result = await this._listSubscriptionLevelByRegion(
      ascLocation,
      options
    );
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listSubscriptionLevelByRegionNext(
        ascLocation,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listSubscriptionLevelByRegionPagingAll(
    ascLocation: string,
    options?: AlertsListSubscriptionLevelByRegionOptionalParams
  ): AsyncIterableIterator<Alert> {
    for await (const page of this.listSubscriptionLevelByRegionPagingPage(
      ascLocation,
      options
    )) {
      yield* page;
    }
  }

  /**
   * List all the alerts that are associated with the resource group that are stored in a specific
   * location
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  public listResourceGroupLevelByRegion(
    ascLocation: string,
    resourceGroupName: string,
    options?: AlertsListResourceGroupLevelByRegionOptionalParams
  ): PagedAsyncIterableIterator<Alert> {
    const iter = this.listResourceGroupLevelByRegionPagingAll(
      ascLocation,
      resourceGroupName,
      options
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: () => {
        return this.listResourceGroupLevelByRegionPagingPage(
          ascLocation,
          resourceGroupName,
          options
        );
      }
    };
  }

  private async *listResourceGroupLevelByRegionPagingPage(
    ascLocation: string,
    resourceGroupName: string,
    options?: AlertsListResourceGroupLevelByRegionOptionalParams
  ): AsyncIterableIterator<Alert[]> {
    let result = await this._listResourceGroupLevelByRegion(
      ascLocation,
      resourceGroupName,
      options
    );
    yield result.value || [];
    let continuationToken = result.nextLink;
    while (continuationToken) {
      result = await this._listResourceGroupLevelByRegionNext(
        ascLocation,
        resourceGroupName,
        continuationToken,
        options
      );
      continuationToken = result.nextLink;
      yield result.value || [];
    }
  }

  private async *listResourceGroupLevelByRegionPagingAll(
    ascLocation: string,
    resourceGroupName: string,
    options?: AlertsListResourceGroupLevelByRegionOptionalParams
  ): AsyncIterableIterator<Alert> {
    for await (const page of this.listResourceGroupLevelByRegionPagingPage(
      ascLocation,
      resourceGroupName,
      options
    )) {
      yield* page;
    }
  }

  /**
   * List all the alerts that are associated with the subscription
   * @param options The options parameters.
   */
  private _list(
    options?: AlertsListOptionalParams
  ): Promise<AlertsListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * List all the alerts that are associated with the resource group
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  private _listByResourceGroup(
    resourceGroupName: string,
    options?: AlertsListByResourceGroupOptionalParams
  ): Promise<AlertsListByResourceGroupResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listByResourceGroupOperationSpec
    );
  }

  /**
   * List all the alerts that are associated with the subscription that are stored in a specific location
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param options The options parameters.
   */
  private _listSubscriptionLevelByRegion(
    ascLocation: string,
    options?: AlertsListSubscriptionLevelByRegionOptionalParams
  ): Promise<AlertsListSubscriptionLevelByRegionResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, options },
      listSubscriptionLevelByRegionOperationSpec
    );
  }

  /**
   * List all the alerts that are associated with the resource group that are stored in a specific
   * location
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  private _listResourceGroupLevelByRegion(
    ascLocation: string,
    resourceGroupName: string,
    options?: AlertsListResourceGroupLevelByRegionOptionalParams
  ): Promise<AlertsListResourceGroupLevelByRegionResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, resourceGroupName, options },
      listResourceGroupLevelByRegionOperationSpec
    );
  }

  /**
   * Get an alert that is associated with a subscription
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param options The options parameters.
   */
  getSubscriptionLevel(
    ascLocation: string,
    alertName: string,
    options?: AlertsGetSubscriptionLevelOptionalParams
  ): Promise<AlertsGetSubscriptionLevelResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, options },
      getSubscriptionLevelOperationSpec
    );
  }

  /**
   * Get an alert that is associated a resource group or a resource in a resource group
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  getResourceGroupLevel(
    ascLocation: string,
    alertName: string,
    resourceGroupName: string,
    options?: AlertsGetResourceGroupLevelOptionalParams
  ): Promise<AlertsGetResourceGroupLevelResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, resourceGroupName, options },
      getResourceGroupLevelOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param options The options parameters.
   */
  updateSubscriptionLevelStateToDismiss(
    ascLocation: string,
    alertName: string,
    options?: AlertsUpdateSubscriptionLevelStateToDismissOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, options },
      updateSubscriptionLevelStateToDismissOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param options The options parameters.
   */
  updateSubscriptionLevelStateToResolve(
    ascLocation: string,
    alertName: string,
    options?: AlertsUpdateSubscriptionLevelStateToResolveOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, options },
      updateSubscriptionLevelStateToResolveOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param options The options parameters.
   */
  updateSubscriptionLevelStateToActivate(
    ascLocation: string,
    alertName: string,
    options?: AlertsUpdateSubscriptionLevelStateToActivateOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, options },
      updateSubscriptionLevelStateToActivateOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  updateResourceGroupLevelStateToResolve(
    ascLocation: string,
    alertName: string,
    resourceGroupName: string,
    options?: AlertsUpdateResourceGroupLevelStateToResolveOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, resourceGroupName, options },
      updateResourceGroupLevelStateToResolveOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  updateResourceGroupLevelStateToDismiss(
    ascLocation: string,
    alertName: string,
    resourceGroupName: string,
    options?: AlertsUpdateResourceGroupLevelStateToDismissOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, resourceGroupName, options },
      updateResourceGroupLevelStateToDismissOperationSpec
    );
  }

  /**
   * Update the alert's state
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertName Name of the alert object
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param options The options parameters.
   */
  updateResourceGroupLevelStateToActivate(
    ascLocation: string,
    alertName: string,
    resourceGroupName: string,
    options?: AlertsUpdateResourceGroupLevelStateToActivateOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertName, resourceGroupName, options },
      updateResourceGroupLevelStateToActivateOperationSpec
    );
  }

  /**
   * Simulate security alerts
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param alertSimulatorRequestBody Alert Simulator Request Properties
   * @param options The options parameters.
   */
  simulate(
    ascLocation: string,
    alertSimulatorRequestBody: AlertSimulatorRequestBody,
    options?: AlertsSimulateOptionalParams
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { ascLocation, alertSimulatorRequestBody, options },
      simulateOperationSpec
    );
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: AlertsListNextOptionalParams
  ): Promise<AlertsListNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listNextOperationSpec
    );
  }

  /**
   * ListByResourceGroupNext
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param nextLink The nextLink from the previous successful call to the ListByResourceGroup method.
   * @param options The options parameters.
   */
  private _listByResourceGroupNext(
    resourceGroupName: string,
    nextLink: string,
    options?: AlertsListByResourceGroupNextOptionalParams
  ): Promise<AlertsListByResourceGroupNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listByResourceGroupNextOperationSpec
    );
  }

  /**
   * ListSubscriptionLevelByRegionNext
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param nextLink The nextLink from the previous successful call to the ListSubscriptionLevelByRegion
   *                 method.
   * @param options The options parameters.
   */
  private _listSubscriptionLevelByRegionNext(
    ascLocation: string,
    nextLink: string,
    options?: AlertsListSubscriptionLevelByRegionNextOptionalParams
  ): Promise<AlertsListSubscriptionLevelByRegionNextResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, nextLink, options },
      listSubscriptionLevelByRegionNextOperationSpec
    );
  }

  /**
   * ListResourceGroupLevelByRegionNext
   * @param ascLocation The location where ASC stores the data of the subscription. can be retrieved from
   *                    Get locations
   * @param resourceGroupName The name of the resource group within the user's subscription. The name is
   *                          case insensitive.
   * @param nextLink The nextLink from the previous successful call to the ListResourceGroupLevelByRegion
   *                 method.
   * @param options The options parameters.
   */
  private _listResourceGroupLevelByRegionNext(
    ascLocation: string,
    resourceGroupName: string,
    nextLink: string,
    options?: AlertsListResourceGroupLevelByRegionNextOptionalParams
  ): Promise<AlertsListResourceGroupLevelByRegionNextResponse> {
    return this.client.sendOperationRequest(
      { ascLocation, resourceGroupName, nextLink, options },
      listResourceGroupLevelByRegionNextOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Security/alerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer
};
const listByResourceGroupOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/alerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listSubscriptionLevelByRegionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listResourceGroupLevelByRegionOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/locations/{ascLocation}/alerts",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.ascLocation
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getSubscriptionLevelOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Alert
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const getResourceGroupLevelOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Alert
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateSubscriptionLevelStateToDismissOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/dismiss",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateSubscriptionLevelStateToResolveOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/resolve",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateSubscriptionLevelStateToActivateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/activate",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateResourceGroupLevelStateToResolveOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/resolve",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateResourceGroupLevelStateToDismissOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/dismiss",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const updateResourceGroupLevelStateToActivateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Security/locations/{ascLocation}/alerts/{alertName}/activate",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.ascLocation,
    Parameters.alertName
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const simulateOperationSpec: coreClient.OperationSpec = {
  path:
    "/subscriptions/{subscriptionId}/providers/Microsoft.Security/locations/{ascLocation}/alerts/default/simulate",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  requestBody: Parameters.alertSimulatorRequestBody,
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.ascLocation
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listByResourceGroupNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listSubscriptionLevelByRegionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.ascLocation
  ],
  headerParameters: [Parameters.accept],
  serializer
};
const listResourceGroupLevelByRegionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AlertList
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  queryParameters: [Parameters.apiVersion13],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.nextLink,
    Parameters.ascLocation
  ],
  headerParameters: [Parameters.accept],
  serializer
};
